import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Table,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Typography,
  App,
  Space,
  Radio,
} from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import {
  supplierInvoiceService,
  type OpenBillableLineDTO,
  type OpenBillableServiceReceiptDTO,
  type CreateSupplierInvoiceDTO,
  type UnclearedPoOverhead,
  type OpenAdvanceRow,
} from '@/services/supplierInvoice.service';
import { goodsReceiptNoteService } from '@/services/goodsReceiptNote.service';
import { serviceReceiptService } from '@/services/serviceReceipt.service';
import { projectService } from '@/services/project.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { headerProjectLabel } from '@/utils/projectDisplay';
import { BudgetCodeLabel } from '@/components/project/BudgetCodeLabel';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { LineSpecificationsText } from '@/components/procurement/LineSpecifications';

const { Title } = Typography;

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

const CreateSupplierInvoicePage: React.FC = () => {
  const { t } = useTranslation('procurement');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const grnIdFromUrl = searchParams.get('grn_id')?.trim() || undefined;
  const srIdFromUrl = searchParams.get('service_receipt_id')?.trim() || undefined;
  const { message } = App.useApp();
  const { isCriticalCode } = useCriticalItemLookup();
  const [form] = Form.useForm();
  const supplierId = Form.useWatch('supplier_id', form);
  const [billSource, setBillSource] = useState<'grn' | 'service_receipt'>(
    srIdFromUrl ? 'service_receipt' : 'grn'
  );
  const [selectedLines, setSelectedLines] = useState<Array<OpenBillableLineDTO & { quantity_billed: number; unit_price: number }>>([]);
  const [selectedSr, setSelectedSr] = useState<OpenBillableServiceReceiptDTO | null>(null);
  const [srBillAmount, setSrBillAmount] = useState<number | null>(null);
  const [selectedOverheadKeys, setSelectedOverheadKeys] = useState<React.Key[]>([]);
  const [overheadAmounts, setOverheadAmounts] = useState<Record<string, number>>({});
  const [overheadSeedKey, setOverheadSeedKey] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [advanceAmounts, setAdvanceAmounts] = useState<Record<string, number>>({});

  const { data: openAdvances = [] } = useQuery({
    queryKey: ['open-advances', supplierId, null],
    queryFn: () => supplierInvoiceService.getOpenAdvances(supplierId as string),
    enabled: !!supplierId,
  });

  useEffect(() => {
    setAdvanceAmounts({});
  }, [supplierId]);

  const savePlannedAdvances = async (invoiceId: string) => {
    const allocations = Object.entries(advanceAmounts)
      .filter(([, amt]) => Number(amt) > 0)
      .map(([payment_voucher_id, amount]) => ({ payment_voucher_id, amount: Number(amount) }));
    if (!allocations.length) return;
    try {
      await supplierInvoiceService.setPlannedAdvances(invoiceId, allocations);
    } catch (e: any) {
      message.warning(
        t('advancesNotSaved', { reason: e?.response?.data?.message || t('unknownError') })
      );
    }
  };

  const { data: suppliersRaw = [] } = useQuery({
    queryKey: ['procurement-suppliers-list'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrap<any[]>(res) ?? [];
    },
  });
  const supplierOptions = useMemo(
    () => (Array.isArray(suppliersRaw) ? suppliersRaw.map((s: any) => ({ value: s.id, label: s.supplier_name ?? s.id })) : []),
    [suppliersRaw]
  );

  const { data: grnFromUrl } = useQuery({
    queryKey: ['grn-for-supplier-invoice', grnIdFromUrl],
    queryFn: () => goodsReceiptNoteService.getById(grnIdFromUrl!),
    enabled: !!grnIdFromUrl && billSource === 'grn',
  });

  const { data: srFromUrl } = useQuery({
    queryKey: ['sr-for-supplier-invoice', srIdFromUrl],
    queryFn: () => serviceReceiptService.getById(srIdFromUrl!),
    enabled: !!srIdFromUrl && billSource === 'service_receipt',
  });

  useEffect(() => {
    if (grnFromUrl?.supplier_id) {
      form.setFieldsValue({ supplier_id: grnFromUrl.supplier_id });
    }
    if (grnFromUrl?.department_id) {
      form.setFieldsValue({ department_id: grnFromUrl.department_id });
    }
  }, [grnFromUrl?.supplier_id, grnFromUrl?.department_id, form]);

  useEffect(() => {
    if (srFromUrl?.supplier_id) {
      form.setFieldsValue({ supplier_id: srFromUrl.supplier_id });
    }
    if (srFromUrl?.department_id) {
      form.setFieldsValue({ department_id: srFromUrl.department_id });
    }
  }, [srFromUrl?.supplier_id, srFromUrl?.department_id, form]);

  const { data: openLines = [], isLoading: openLinesLoading } = useQuery({
    queryKey: ['supplier-invoice-open-lines', supplierId],
    queryFn: () => supplierInvoiceService.getOpenLines(supplierId!),
    enabled: !!supplierId && billSource === 'grn',
  });

  const { data: openSrs = [], isLoading: openSrsLoading } = useQuery({
    queryKey: ['supplier-invoice-open-srs', supplierId],
    queryFn: () => supplierInvoiceService.getOpenBillableServiceReceipts(supplierId!),
    enabled: !!supplierId && billSource === 'service_receipt',
  });

  useEffect(() => {
    if (billSource !== 'service_receipt' || !openSrs.length) return;
    const match = srIdFromUrl
      ? openSrs.find((r) => r.service_receipt_id === srIdFromUrl)
      : openSrs[0];
    if (match) {
      setSelectedSr(match);
      setSrBillAmount(match.pending_value);
      if (match.department_id) form.setFieldsValue({ department_id: match.department_id });
    }
  }, [openSrs, srIdFromUrl, billSource, form]);

  const openLinesForTable = useMemo(
    () => (grnIdFromUrl ? openLines.filter((l) => l.grn_id === grnIdFromUrl) : openLines),
    [openLines, grnIdFromUrl]
  );

  const overheadGrnIds = useMemo(() => {
    const ids = new Set<string>();
    for (const l of selectedLines) {
      if (l.grn_id) ids.add(l.grn_id);
    }
    if (grnIdFromUrl) ids.add(grnIdFromUrl);
    return [...ids];
  }, [selectedLines, grnIdFromUrl]);

  const { data: unclearedOverheads = [], isLoading: overheadsLoading } = useQuery({
    queryKey: ['supplier-invoice-uncleared-overheads', supplierId, overheadGrnIds.join(',')],
    queryFn: () =>
      supplierInvoiceService.getUnclearedOverheads({
        grn_ids: overheadGrnIds,
        supplier_id: supplierId,
      }),
    enabled: !!supplierId && overheadGrnIds.length > 0,
  });

  useEffect(() => {
    const seed = unclearedOverheads.map((o) => `${o.id}:${o.remaining_amount}`).join('|');
    if (seed === overheadSeedKey) return;
    setOverheadSeedKey(seed);
    if (!unclearedOverheads.length) {
      setSelectedOverheadKeys([]);
      setOverheadAmounts({});
      return;
    }
    setSelectedOverheadKeys(unclearedOverheads.map((o) => o.id));
    setOverheadAmounts(
      Object.fromEntries(unclearedOverheads.map((o) => [o.id, Number(o.remaining_amount) || 0]))
    );
  }, [unclearedOverheads, overheadSeedKey]);

  const { data: coaList = [] } = useQuery({
    queryKey: ['chart-of-accounts-ap'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const list = unwrap<any[]>(res) ?? [];
      return Array.isArray(list) ? list : [];
    },
  });
  const debitAccountOptions = useMemo(
    () => coaList.filter((a: any) => !a.is_group).map((a: any) => ({ value: a.id, label: `${a.account_code} – ${a.account_name}` })),
    [coaList]
  );

  const { data: projectsList = [] } = useQuery({
    queryKey: ['projects-selection'],
    queryFn: () => projectService.getProjectsForSelection(),
  });

  const projectMap = useMemo(() => {
    const map = new Map<string, string>();
    if (Array.isArray(projectsList)) {
      for (const p of projectsList) {
        const label = [p.project_number, p.project_name].filter(Boolean).join(' – ') || p.project_number || p.project_name || '';
        if (p.id) map.set(p.id, label);
      }
    }
    return map;
  }, [projectsList]);

  const renderDescriptionCell = (desc: string, row: OpenBillableLineDTO) => (
    <>
      <div>{desc}</div>
      <LineSpecificationsText value={row.specifications} />
    </>
  );

  const renderProjectCell = (row: {
    project_id?: string | null;
    project_code?: string | null;
    project_number?: string | null;
    project_name?: string | null;
  }) => {
    const code = row.project_code || row.project_number;
    if (code && row.project_name) return `${code} – ${row.project_name}`;
    if (code) return code;
    if (row.project_name) return row.project_name;
    if (row.project_id && projectMap.has(row.project_id)) {
      const mapped = projectMap.get(row.project_id);
      if (mapped) return mapped;
    }
    return '—';
  };

  const addLine = (line: OpenBillableLineDTO) => {
    if (selectedLines.some((l) => l.grn_item_id === line.grn_item_id)) return;
    setSelectedLines((prev) => [
      ...prev,
      {
        ...line,
        quantity_billed: Number(line.pending_quantity) > 0 ? Number(line.pending_quantity) : 0,
        unit_price: line.unit_price,
      },
    ]);
  };

  const removeLine = (grnItemId: string) => {
    setSelectedLines((prev) => prev.filter((l) => l.grn_item_id !== grnItemId));
  };

  const setLineQty = (grnItemId: string, quantity_billed: number) => {
    setSelectedLines((prev) =>
      prev.map((l) => (l.grn_item_id === grnItemId ? { ...l, quantity_billed } : l))
    );
  };

  const setLinePrice = (grnItemId: string, unit_price: number) => {
    setSelectedLines((prev) =>
      prev.map((l) => (l.grn_item_id === grnItemId ? { ...l, unit_price } : l))
    );
  };

  const subtotal = useMemo(
    () => selectedLines.reduce((s, l) => s + l.quantity_billed * l.unit_price, 0),
    [selectedLines]
  );
  /** VAT from PO item-wise: no manual entry */
  const vatFromPO = useMemo(
    () =>
      selectedLines.reduce((s, l) => {
        const amount = l.quantity_billed * l.unit_price;
        const pct = l.vat_applicable !== false ? Number(l.vat_percentage ?? 0) : 0;
        return s + (pct > 0 ? Math.round(amount * (pct / 100) * 100) / 100 : 0);
      }, 0),
    [selectedLines]
  );
  const discount = Form.useWatch('discount_amount', form) ?? 0;
  const freight = Form.useWatch('freight_charges', form) ?? 0;
  const other = Form.useWatch('other_charges', form) ?? 0;
  const overheadTotal = useMemo(
    () =>
      selectedOverheadKeys.reduce<number>((s, key) => {
        const id = String(key);
        return s + (Number(overheadAmounts[id]) || 0);
      }, 0),
    [selectedOverheadKeys, overheadAmounts]
  );
  const totalAfterDiscount = subtotal - Number(discount);
  const grandTotal = totalAfterDiscount + vatFromPO + Number(freight) + Number(other) + overheadTotal;

  const srBillSubtotalGross = Number(srBillAmount ?? selectedSr?.pending_value ?? 0);
  const srVatFromPo = useMemo(() => {
    if (!selectedSr || !(srBillSubtotalGross > 0)) return 0;
    const pct =
      selectedSr.vat_applicable !== false ? Number(selectedSr.vat_percentage ?? 0) : 0;
    if (!(pct > 0)) return 0;
    // SR pending is VAT-inclusive (PO grand_total / milestone basis)
    if (
      selectedSr.pending_vat != null &&
      Math.abs(srBillSubtotalGross - Number(selectedSr.pending_value)) < 0.01
    ) {
      return Number(selectedSr.pending_vat);
    }
    const exVat = Math.round((srBillSubtotalGross / (1 + pct / 100)) * 100) / 100;
    return Math.round((srBillSubtotalGross - exVat) * 100) / 100;
  }, [selectedSr, srBillSubtotalGross]);
  const srExVat = useMemo(() => {
    if (!(srBillSubtotalGross > 0)) return 0;
    return Math.round((srBillSubtotalGross - srVatFromPo) * 100) / 100;
  }, [srBillSubtotalGross, srVatFromPo]);
  const srGrandTotal =
    srExVat - Number(discount) + srVatFromPo + Number(freight) + Number(other);

  const overheadRowSelection: TableRowSelection<UnclearedPoOverhead> = {
    selectedRowKeys: selectedOverheadKeys,
    onChange: (keys) => setSelectedOverheadKeys(keys),
  };

  const handleSubmit = async () => {
    if (!supplierId) {
      message.warning(t('selectASupplier'));
      return;
    }
    if (billSource === 'service_receipt') {
      if (!selectedSr) {
        message.warning(t('selectPostedServiceReceipt'));
        return;
      }
      const amount = Number(srBillAmount ?? selectedSr.pending_value);
      if (!(amount > 0)) {
        message.warning(t('enterBillAmountGtZero'));
        return;
      }
      try {
        await form.validateFields();
        const values = form.getFieldsValue();
        const dto: CreateSupplierInvoiceDTO = {
          supplier_id: supplierId,
          supplier_invoice_number: values.supplier_invoice_number,
          supplier_invoice_date: values.supplier_invoice_date,
          received_date: values.received_date,
          due_date: values.due_date,
          payment_terms: values.payment_terms,
          debit_account_id: values.debit_account_id,
          department_id: values.department_id,
          notes: values.notes,
          invoice_type: 'ServiceReceiptMatched',
          service_receipt_id: selectedSr.service_receipt_id,
          po_milestone_id: selectedSr.po_milestone_id,
          amount,
          discount_amount: values.discount_amount ?? 0,
          freight_charges: values.freight_charges ?? 0,
          other_charges: values.other_charges ?? 0,
        };
        setSubmitLoading(true);
        const created = await supplierInvoiceService.create(dto);
        await savePlannedAdvances(created.id);
        message.success(t('purchaseBillCreatedFromSr'));
        navigate(`/scm/procurement/supplier-invoices/${created.id}`);
      } catch (e: any) {
        if (e?.errorFields) return;
        message.error(e?.response?.data?.message || t('failedToCreate'));
      } finally {
        setSubmitLoading(false);
      }
      return;
    }

    if (selectedLines.length === 0) {
      message.warning(t('addAtLeastOneLine'));
      return;
    }
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      const dto: CreateSupplierInvoiceDTO = {
        supplier_id: supplierId,
        supplier_invoice_number: values.supplier_invoice_number,
        supplier_invoice_date: values.supplier_invoice_date,
        received_date: values.received_date,
        due_date: values.due_date,
        payment_terms: values.payment_terms,
        debit_account_id: values.debit_account_id,
        department_id: values.department_id,
        notes: values.notes,
        discount_amount: values.discount_amount ?? 0,
        discount_account_id: Number(values.discount_amount ?? 0) > 0 ? values.discount_account_id ?? null : null,
        freight_charges: values.freight_charges ?? 0,
        other_charges: values.other_charges ?? 0,
        lines: selectedLines.map((l) => ({
          grn_item_id: l.grn_item_id,
          quantity_billed: l.quantity_billed,
          unit_price: l.unit_price,
        })),
        overhead_lines: selectedOverheadKeys
          .map((key) => {
            const id = String(key);
            return { po_overhead_id: id, amount: Number(overheadAmounts[id]) || 0 };
          })
          .filter((o) => o.amount > 0),
      };
      setSubmitLoading(true);
      const created = await supplierInvoiceService.create(dto);
      await savePlannedAdvances(created.id);
      message.success(t('purchaseBillCreated'));
      navigate(`/scm/procurement/supplier-invoices/${created.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || t('failedToCreate'));
    } finally {
      setSubmitLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{ padding: 24 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/scm/procurement/supplier-invoices')} style={{ marginBottom: 16 }}>
        {t('backToSupplierInvoices')}
      </Button>
      <Title level={4} style={{ marginBottom: 16 }}>
        {t('createPurchaseBill')}
      </Title>
      <Radio.Group
        value={billSource}
        onChange={(e) => {
          const next = e.target.value as 'grn' | 'service_receipt';
          setBillSource(next);
          setSelectedLines([]);
          setSelectedSr(null);
          setSrBillAmount(null);
          setSelectedOverheadKeys([]);
        }}
        style={{ marginBottom: 16 }}
        optionType="button"
        options={[
          { label: t('fromGrn'), value: 'grn' },
          { label: t('fromServiceReceipt'), value: 'service_receipt' },
        ]}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          supplier_invoice_date: today,
          received_date: today,
          due_date: today,
          discount_amount: 0,
          freight_charges: 0,
          other_charges: 0,
        }}
      >
        <Card title={t("header")} style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="supplier_id" label={t("supplier")} rules={[{ required: true, message: t("supplierRequired") }]}>
                <Select
                  placeholder={t('selectSupplier')}
                  options={supplierOptions}
                  onChange={() => {
                    setSelectedLines([]);
                    setSelectedOverheadKeys([]);
                    setOverheadAmounts({});
                    setOverheadSeedKey('');
                    if (form.getFieldValue('supplier_invoice_number')) {
                      form.validateFields(['supplier_invoice_number']);
                    }
                  }}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="supplier_invoice_number"
                label={t("supplierInvoiceNumber")}
                rules={[
                  { required: true, message: t("supplierInvoiceNumberRequired") },
                  {
                    validator: async (_, value) => {
                      const trimmed = value?.trim();
                      if (!trimmed || !supplierId) return;
                      try {
                        const check = await supplierInvoiceService.checkDuplicate(supplierId, trimmed);
                        if (check.duplicate) {
                          return Promise.reject(
                            new Error(
                              t('duplicateSupplierInvoice', {
                                number: trimmed,
                                onBill: check.existing_bill_number
                                  ? t('duplicateOnBill', { bill: check.existing_bill_number })
                                  : '',
                                deleted: check.is_deleted ? t('duplicateDeleted') : '',
                              })
                            )
                          );
                        }
                      } catch {
                        // ignore network check error to avoid blocking valid submits
                      }
                    },
                  },
                ]}
              >
                <Input
                  placeholder={t('vendorInvoiceNumber')}
                  onBlur={() => {
                    const val = form.getFieldValue('supplier_invoice_number');
                    if (val && supplierId) {
                      form.validateFields(['supplier_invoice_number']);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="supplier_invoice_date" label={t("invoiceDate")} rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="received_date" label={t("billDate")} rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="due_date" label={t("dueDate")} rules={[{ required: true }]}>
                <Input type="date" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="debit_account_id" label={t("creditAccountAp")}>
                <Select placeholder={t('defaultAp2000')} options={debitAccountOptions} allowClear showSearch optionFilterProp="label" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="department_id"
                label={t("department")}
                rules={[{ required: true, message: t('departmentRequired') }]}
              >
                <DepartmentSelect />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="payment_terms" label={t("paymentTerms")}>
                <Input placeholder={t('paymentTermsExample')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="discount_amount" label={t("discount")}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            {billSource === 'grn' && Number(discount) > 0 && (
              <Col xs={24} md={8}>
                <Form.Item
                  name="discount_account_id"
                  label={t("discountAccount")}
                  tooltip={t("discountAccountTooltip")}
                >
                  <Select
                    placeholder={t('discountAccountPlaceholder')}
                    options={debitAccountOptions}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>
            )}
            <Col xs={24} md={8}>
              <Form.Item label={t("vatFromPo")}>
                <Input value={selectedLines.length ? vatFromPO.toFixed(2) : '0.00'} readOnly style={{ background: '#fafafa' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="freight_charges" label={t("freight")}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item name="other_charges" label={t("otherCharges")}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="notes" label={t("notes")}>
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {supplierId && billSource === 'service_receipt' && (
          <Card title={t("openBillableServiceReceipts")} style={{ marginBottom: 16 }}>
            <p style={{ color: '#666', marginBottom: 12 }}>
              {t('openBillableServiceReceiptsHint')}
            </p>
            <Table
              size="small"
              rowKey="service_receipt_id"
              loading={openSrsLoading}
              dataSource={openSrs}
              pagination={false}
              locale={{ emptyText: t('noOpenServiceReceipts') }}
              rowSelection={{
                type: 'radio',
                selectedRowKeys: selectedSr ? [selectedSr.service_receipt_id] : [],
                onChange: (_, rows) => {
                  const row = rows[0] as OpenBillableServiceReceiptDTO | undefined;
                  setSelectedSr(row ?? null);
                  setSrBillAmount(row?.pending_value ?? null);
                  if (row?.department_id) form.setFieldsValue({ department_id: row.department_id });
                },
              }}
              columns={[
                { title: t('srNumber'), dataIndex: 'sr_number' },
                { title: t('date'), dataIndex: 'sr_date' },
                { title: t('purchaseOrderShort'), dataIndex: 'po_number' },
                { title: t('milestone'), dataIndex: 'milestone_label' },
                {
                  title: t('pending'),
                  dataIndex: 'pending_value',
                  align: 'right' as const,
                  render: (v: number) => Number(v ?? 0).toFixed(2),
                },
                {
                  title: t('vatPercent'),
                  dataIndex: 'vat_percentage',
                  align: 'right' as const,
                  width: 80,
                  render: (v: number, row: OpenBillableServiceReceiptDTO) =>
                    row.vat_applicable === false || !(Number(v) > 0) ? '—' : `${Number(v).toFixed(0)}%`,
                },
                {
                  title: t('estVat'),
                  dataIndex: 'pending_vat',
                  align: 'right' as const,
                  render: (v: number) => Number(v ?? 0).toFixed(2),
                },
              ]}
            />
            {selectedSr && (
              <Space style={{ marginTop: 12 }}>
                <span>{t('billAmount')}</span>
                <InputNumber
                  min={0.01}
                  max={selectedSr.pending_value}
                  value={srBillAmount ?? selectedSr.pending_value}
                  onChange={(v) => setSrBillAmount(v == null ? null : Number(v))}
                  precision={2}
                  style={{ width: 160 }}
                />
                <span>{t('ofPending', { amount: Number(selectedSr.pending_value).toFixed(2) })}</span>
              </Space>
            )}
          </Card>
        )}

        {supplierId && billSource === 'grn' && (
          <Card title={t("openBillableLines")} style={{ marginBottom: 16 }}>
            <p style={{ color: '#666', marginBottom: 12 }}>
              {t('openBillableLinesHint')}
            </p>
            {grnIdFromUrl && grnFromUrl && (
              <p style={{ marginBottom: 8 }}>
                {t('preSelectedGrn')} <strong>{grnFromUrl.grn_number}</strong>
              </p>
            )}
            {(grnFromUrl || selectedLines.length > 0) && (
              <Form.Item label={t("project")}>
                <Input
                  readOnly
                  value={headerProjectLabel(
                    grnFromUrl ?? undefined,
                    selectedLines.length ? selectedLines : grnFromUrl?.items
                  )}
                />
              </Form.Item>
            )}
            {openLinesLoading ? (
              <p>{tc('loading')}</p>
            ) : openLinesForTable.length === 0 ? (
              <p>{grnIdFromUrl ? t('noOpenBillableLinesOnGrn') : t('noOpenBillableLines')}</p>
            ) : (
              <Table
                size="small"
                dataSource={openLinesForTable.map((o) => ({ ...o, key: o.grn_item_id }))}
                rowClassName={(row: OpenBillableLineDTO) =>
                  getCriticalItemRowClassName({ is_critical: isCriticalCode(row.item_code) })
                }
                columns={[
                  { title: t('grn'), dataIndex: 'grn_number', key: 'grn_number', width: 110 },
                  { title: t('purchaseOrderShort'), dataIndex: 'po_number', key: 'po_number', width: 110 },
                  {
                    title: t('item'),
                    dataIndex: 'item_code',
                    key: 'item_code',
                    width: 140,
                    render: (code: string, row: OpenBillableLineDTO) => (
                      <ItemCodeWithCritical code={code} critical={isCriticalCode(row.item_code)} />
                    ),
                  },
                  { title: t('description'), dataIndex: 'item_description', key: 'item_description', render: renderDescriptionCell },
                  {
                    title: t('supplierDescription'),
                    dataIndex: 'supplier_description',
                    key: 'supplier_description',
                    render: (v: string, row: OpenBillableLineDTO) => v || row.item_description || '—',
                  },
                  {
                    title: t('project'),
                    key: 'project',
                    width: 140,
                    ellipsis: true,
                    render: (_: unknown, row: OpenBillableLineDTO) => renderProjectCell(row),
                  },
                  {
                    title: t('wbs'),
                    key: 'wbs',
                    width: 130,
                    ellipsis: true,
                    render: (_: unknown, row: OpenBillableLineDTO) => (
                      <BudgetCodeLabel projectId={row.project_id} nodeId={row.wbs_node_id} kind="wbs" />
                    ),
                  },
                  {
                    title: t('cat'),
                    key: 'cat',
                    width: 90,
                    render: (_: unknown, row: OpenBillableLineDTO) => row.cost_category || '—',
                  },
                  { title: t('pending'), dataIndex: 'pending_quantity', key: 'pending_quantity', width: 80, align: 'right' },
                  { title: t('rate'), dataIndex: 'unit_price', key: 'unit_price', width: 90, align: 'right', render: (v: number) => Number(v).toFixed(2) },
                  {
                    title: t('action'),
                    key: 'action',
                    width: 80,
                    render: (_: any, row: OpenBillableLineDTO) => (
                      <Button
                        type="link"
                        size="small"
                        disabled={selectedLines.some((l) => l.grn_item_id === row.grn_item_id)}
                        onClick={() => addLine(row)}
                      >
                        {tc('add')}
                      </Button>
                    ),
                  },
                ]}
                pagination={false}
                scroll={{ x: 700 }}
              />
            )}
          </Card>
        )}

        {selectedLines.length > 0 && (
          <Card title={t("billLines")} style={{ marginBottom: 16 }}>
            <Table
              size="small"
              dataSource={selectedLines.map((l) => ({ ...l, key: l.grn_item_id }))}
              rowClassName={(row: OpenBillableLineDTO) =>
                getCriticalItemRowClassName({ is_critical: isCriticalCode(row.item_code) })
              }
              columns={[
                { title: t('grn'), dataIndex: 'grn_number', key: 'grn_number', width: 100 },
                {
                  title: t('item'),
                  dataIndex: 'item_code',
                  key: 'item_code',
                  width: 140,
                  render: (code: string, row: OpenBillableLineDTO) => (
                    <ItemCodeWithCritical code={code} critical={isCriticalCode(row.item_code)} />
                  ),
                },
                { title: t('description'), dataIndex: 'item_description', key: 'item_description', render: renderDescriptionCell },
                {
                  title: t('project'),
                  key: 'project',
                  width: 140,
                  ellipsis: true,
                  render: (_: unknown, row: OpenBillableLineDTO) => renderProjectCell(row),
                },
                {
                  title: t('wbs'),
                  key: 'wbs',
                  width: 130,
                  ellipsis: true,
                  render: (_: unknown, row: OpenBillableLineDTO) => (
                    <BudgetCodeLabel projectId={row.project_id} nodeId={row.wbs_node_id} kind="wbs" />
                  ),
                },
                {
                  title: t('cat'),
                  key: 'cat',
                  width: 90,
                  render: (_: unknown, row: OpenBillableLineDTO) => row.cost_category || '—',
                },
                {
                  title: t('qtyToBill'),
                  key: 'quantity_billed',
                  width: 120,
                  render: (_: any, row: any) => (
                    <InputNumber
                      min={0.0001}
                      max={row.pending_quantity}
                      value={row.quantity_billed}
                      onChange={(v) => setLineQty(row.grn_item_id, Number(v) || 0)}
                      style={{ width: '100%' }}
                    />
                  ),
                },
                {
                  title: t('unitPrice'),
                  key: 'unit_price',
                  width: 120,
                  render: (_: any, row: any) => (
                    <InputNumber
                      min={0}
                      value={row.unit_price}
                      onChange={(v) => setLinePrice(row.grn_item_id, Number(v) || 0)}
                      style={{ width: '100%' }}
                    />
                  ),
                },
                {
                  title: t('amount'),
                  key: 'amount',
                  width: 100,
                  align: 'right',
                  render: (_: any, row: any) => (row.quantity_billed * row.unit_price).toFixed(2),
                },
                {
                  title: '',
                  key: 'remove',
                  width: 60,
                  render: (_: any, row: any) => (
                    <Button type="link" danger size="small" onClick={() => removeLine(row.grn_item_id)}>
                      {tc('remove')}
                    </Button>
                  ),
                },
              ]}
              pagination={false}
            />
          </Card>
        )}

        {supplierId && overheadGrnIds.length > 0 && (
          <Card title={t("poOverheadsToClear")} style={{ marginBottom: 16 }}>
            <p style={{ color: '#666', marginBottom: 12 }}>
              {t('poOverheadsToClearHint')}
            </p>
            {overheadsLoading ? (
              <p>{tc('loading')}</p>
            ) : unclearedOverheads.length === 0 ? (
              <p>{t('noUnclearedPoOverheads')}</p>
            ) : (
              <Table
                size="small"
                rowKey="id"
                rowSelection={overheadRowSelection}
                dataSource={unclearedOverheads}
                pagination={false}
                columns={[
                  { title: t('purchaseOrderShort'), dataIndex: 'po_number', key: 'po_number', width: 110 },
                  { title: t('type'), dataIndex: 'overhead_type', key: 'overhead_type', width: 120 },
                  {
                    title: t('description'),
                    dataIndex: 'description',
                    key: 'description',
                    ellipsis: true,
                    render: (v: string | null) => v || '—',
                  },
                  {
                    title: t('poAmount'),
                    dataIndex: 'amount',
                    key: 'amount',
                    width: 110,
                    align: 'right' as const,
                    render: (v: number) => Number(v).toFixed(2),
                  },
                  {
                    title: t('remaining'),
                    dataIndex: 'remaining_amount',
                    key: 'remaining_amount',
                    width: 110,
                    align: 'right' as const,
                    render: (v: number) => Number(v).toFixed(2),
                  },
                  {
                    title: t('clearAmount'),
                    key: 'clear_amount',
                    width: 140,
                    render: (_: unknown, row: UnclearedPoOverhead) => (
                      <InputNumber
                        min={0.01}
                        max={Number(row.remaining_amount) || 0}
                        value={overheadAmounts[row.id]}
                        disabled={!selectedOverheadKeys.includes(row.id)}
                        onChange={(v) =>
                          setOverheadAmounts((prev) => ({
                            ...prev,
                            [row.id]: Math.min(Number(v) || 0, Number(row.remaining_amount) || 0),
                          }))
                        }
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                ]}
              />
            )}
          </Card>
        )}

        {(selectedLines.length > 0 || overheadTotal > 0 || (billSource === 'service_receipt' && selectedSr)) && (
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            {billSource === 'service_receipt' && selectedSr ? (
              <>
                <strong>{t('subtotalExVat')}</strong> {srExVat.toFixed(2)}
                {' '}
                &nbsp;|&nbsp; <strong>{t('vatInclInSr', { pct: selectedSr.vat_percentage ? ` ${selectedSr.vat_percentage}%` : '' })}</strong>{' '}
                {srVatFromPo.toFixed(2)} &nbsp;|&nbsp;{' '}
                <strong>{t('grandTotalColon')}</strong> {srGrandTotal.toFixed(2)}
              </>
            ) : (
              <>
            <strong>{t('subtotalColon')}</strong> {subtotal.toFixed(2)}
            {overheadTotal > 0 ? (
              <>
                {' '}
                &nbsp;|&nbsp; <strong>{t('overheadsColon')}</strong> {overheadTotal.toFixed(2)}
              </>
            ) : null}{' '}
            &nbsp;|&nbsp; <strong>{t('vatFromPoColon')}</strong> {vatFromPO.toFixed(2)} &nbsp;|&nbsp;{' '}
            <strong>{t('grandTotalColon')}</strong> {grandTotal.toFixed(2)}
              </>
            )}
          </div>
        )}

        {supplierId && openAdvances.length > 0 ? (
          <Card
            size="small"
            title={t('supplierAdvancesOpen', { count: openAdvances.length })}
            extra={<Typography.Text type="secondary">{t('advancesKnockOffHint')}</Typography.Text>}
            style={{ marginBottom: 16 }}
          >
            <Table
              size="small"
              rowKey="id"
              dataSource={openAdvances}
              pagination={false}
              columns={[
                { title: t('voucherNo'), dataIndex: 'voucher_number', key: 'voucher_number', width: 140 },
                { title: t('date'), dataIndex: 'voucher_date', key: 'date', width: 110 },
                { title: t('purchaseOrderShort'), dataIndex: 'po_number', key: 'po', width: 140, render: (v: string | null) => v || '—' },
                { title: t('openBalance'), dataIndex: 'open_balance', key: 'open', width: 130, align: 'right' as const, render: (v: number) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 }) },
                {
                  title: t('amountToKnockOff'),
                  key: 'amount',
                  width: 170,
                  render: (_: unknown, row: OpenAdvanceRow) => (
                    <InputNumber
                      min={0}
                      max={row.open_balance}
                      step={0.01}
                      precision={2}
                      style={{ width: '100%' }}
                      placeholder="0"
                      value={advanceAmounts[row.id] ?? null}
                      onChange={(v) => setAdvanceAmounts((prev) => ({ ...prev, [row.id]: Number(v) || 0 }))}
                    />
                  ),
                },
              ]}
            />
          </Card>
        ) : null}

        <Space>
          <Button
            type="primary"
            loading={submitLoading}
            onClick={handleSubmit}
            disabled={
              billSource === 'service_receipt' ? !selectedSr : selectedLines.length === 0
            }
          >
            {t('createPurchaseBill')}
          </Button>
          <Button onClick={() => navigate('/scm/procurement/supplier-invoices')}>{tc('cancel')}</Button>
        </Space>
      </Form>
    </div>
  );
};

export default CreateSupplierInvoicePage;
